import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import {
  useDeleteAcademicYearMutation,
  useGetAcademicYearsTemplateQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Link, useNavigate } from "react-router-dom";
import { AcademicYear } from "types/academicYear";
import { ResponsiveButtonLink } from "components/Button";
import { useNotifications } from "hooks/useNotifications";
import { Link as LinkBase, Stack } from "@mui/material";
import { AdminRoutes } from "config/routes";

export const AcademicYearList = () => {
  const navigate = useNavigate();
  const [deleteAcademicYearModal, setDeleteAcademicYearModal] = useState(false);
  const [academicYearIdToDelete, setAcademicYearIdToDelete] = useState("");
  const [academicYears, setAcademicYears] = useState([] as AcademicYear[]);

  const [deleteAcademicYear] = useDeleteAcademicYearMutation();
  const { loading, error, data, refetch } = useGetAcademicYearsTemplateQuery();
  const { notify } = useNotifications();

  useEffect(() => {
    setAcademicYears(
      (data?.academic_year.map((ay) => ({
        ...ay,
        tags: ay.tags?.map((t) => t.tag),
        requirements: ay.requirements.map((r) => r.requirement),
      })) as AcademicYear[]) || [],
    );
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(`/academic-year/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(`/academic-year/edit/${id}`);
        break;
      case TABLE_ACTIONS.DELETE:
        setAcademicYearIdToDelete(id);
        setDeleteAcademicYearModal(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!academicYearIdToDelete || academicYearIdToDelete === "") {
      return;
    }
    await deleteAcademicYear({ variables: { id: academicYearIdToDelete } });
    await refetch();
    setAcademicYearIdToDelete("");
    notify({
      message: Messages.successAcademicYearDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Program Templates"
      loading={loading}
      error={error}
      buttonActions={
        <Stack direction="row" spacing={2}>
          <ResponsiveButtonLink
            to={AdminRoutes.UploadCSVPrograms}
            variant="outlined"
            icon={<FileUploadIcon />}
          >
            Upload CSV File
          </ResponsiveButtonLink>
          <ResponsiveButtonLink to="/academic-year/create" icon={<Add />}>
            New Program
          </ResponsiveButtonLink>
        </Stack>
      }
    >
      <CustomTable
        entities={academicYears}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "academic_year_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  component={Link}
                  style={{ cursor: "pointer" }}
                  to={`/academic-year/${row.academic_year_id}`}
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"academic_year_id"}
      />

      {deleteAcademicYearModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this Program?"}
          show={deleteAcademicYearModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteAcademicYearModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
